import type { NavigationGuardWithThis } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useGlobalStore } from 'src/stores/global'
import { useModelsStore, useAdminModelsStore } from 'src/stores/models'
import { useDigitalTwinsStore } from 'src/stores/digital-twins'
import { useSelectedStore } from 'src/stores/selected'
import { useViewersStore } from 'src/stores/viewers'
import {
  capturePosthogEvent,
  capturePosthogException,
  toErrorMessage
} from 'src/plugins/posthog-events'
import {
  msalInstance,
  isAuthenticated,
  login,
  loginRequest,
  hasTokenQueryParam
} from 'src/services/auth'
import { useParametersStore } from 'src/stores/parameters'
import {
  isFeatureVisible,
  createHideExpressionContext
} from 'src/composables/use-feature-visibility'

export const handleAuthRedirect: NavigationGuardWithThis<undefined> = async () => {
  try {
    await msalInstance.handleRedirectPromise()
    return true
  } catch (e) {
    // if auth failed or redirect was canceled, fall back to the app root.
    // '/' is the Astro marketing site, which this router does not serve.
    console.error('Error handling redirect promise', e)
    capturePosthogException(e, { operation: 'handle authentication redirect' })
    capturePosthogEvent('AuthRedirectError', { error: toErrorMessage(e) })
    return '/app'
  }
}

export const authGuard: NavigationGuardWithThis<undefined> = async (to) => {
  // Allow access if token query param is present (for automation/service principal access)
  if (hasTokenQueryParam()) {
    return true
  }

  if (isAuthenticated()) return true
  // console.debug('Not authenticated. Logging in ... ')
  capturePosthogEvent('NotAuthenticatedLoggingIn')
  await login({
    ...loginRequest,
    // Make sure to redirect to the page we want to navigate to after login (will only be used in redirect mode)
    redirectStartPage: `${globalThis.location.origin}${to.path}`
  })
  // In case this takes too long, we redirect to the environment list (after a
  // delay) instead of showing a blank screen. Not '/': that is the Astro
  // marketing site, which this router does not serve.
  await new Promise<void>((resolve) => {
    setTimeout(() => {
      console.warn('Login timed out. Redirecting to the environment list ...')
      capturePosthogEvent('LoginTimeout')
      resolve()
    }, 2500)
  })
  return '/app'
}

// eslint-disable-next-line sonarjs/function-return-type
export const adminGuard: NavigationGuardWithThis<undefined> = (to) => {
  // Destructure the required properties from the global store
  const { editEnvAllowed, currentMainFeatureId, featureConfigs } = storeToRefs(useGlobalStore())

  // Check if the user is allowed to edit the environment
  if (editEnvAllowed.value) {
    // If the user is allowed to edit the env, allow navigation
    return true
  }

  // If the user is not an owner
  if (to.params.featureId) {
    // If there is a featureId in the navigation parameters, set it as the currentMainFeatureId
    currentMainFeatureId.value = `${to.params.featureId}`
  } else {
    // If there is no featureId in the navigation parameters
    const { mainConfigs } = featureConfigs.value
    // If there are mainConfigs and the first one has a feature
    if (mainConfigs.length && mainConfigs[0].feature) {
      // Redirect to the 'app-feature' route with the environmentId and featureId as parameters
      return {
        name: 'app-feature',
        params: {
          environmentId: to.params.environmentId,
          featureId: mainConfigs[0].feature.$dtId
        },
        query: to.query
      }
    }
  }
}

export const beforeEnvironment: NavigationGuardWithThis<undefined> = async (to) => {
  const globalStore = useGlobalStore()
  const { environmentId, environments, features } = storeToRefs(globalStore)
  const { getEnvironments, preloadAllFeatureQueries, resetEnvironmentScopedState } = globalStore
  const modelsStore = useModelsStore()
  const adminModelsStore = useAdminModelsStore()

  try {
    if (environments.value.length === 0) await getEnvironments()
    else getEnvironments() // Reload environments in background to get latest data

    if (environments.value.some((e) => e.$dtId === to.params.environmentId)) {
      // Access to environment is allowed
      if (environmentId.value === to.params.environmentId) {
        // In the same environment, update models and features but keep the current ones
        // Get models from server
        modelsStore.getModels()
        if (features.value.length === 0) {
          // Features haven't been loaded into the store yet (e.g. a fresh page load on a
          // deep link where the persisted environmentId happens to match the URL) - await
          // so the child route's beforeFeature guard can redirect to the first feature.
          // Use preloadAllFeatureQueries as the single source of truth: it populates both
          // features and featureQueries in one round-trip, avoiding the race between
          // a plain getFeatures call and the fire-and-forget preload.
          await preloadAllFeatureQueries(environmentId.value)
        } else {
          // Features already loaded, refresh in the background without blocking navigation
          void preloadAllFeatureQueries(environmentId.value)
        }
      } else {
        // Environment changed, set new environmentId
        environmentId.value = `${to.params.environmentId}`
        // Reset and clear all stores
        useSelectedStore().setSelectedByIds([])
        resetEnvironmentScopedState()
        modelsStore.models = []
        const twinsStore = useDigitalTwinsStore()
        twinsStore.twins = {}
        twinsStore.relationships = {}
        useParametersStore().clear()
        useViewersStore().reset()
        // Get models from server (not awaited)
        modelsStore.getModels()
        // Get features + queries from server (await to avoid seeing things from previous env)
        await preloadAllFeatureQueries(environmentId.value)
      }
      // Kick off the adminModels load (not awaited — components that classify twins by
      // DTDL model await `ensureModelsLoaded` themselves, and it shares this request)
      void adminModelsStore.ensureModelsLoaded()
      return true
    } else {
      console.error(`Access to environment ${to.params.environmentId} not allowed`)
      capturePosthogEvent('EnvironmentAccessDenied', {
        environment_id: `${to.params.environmentId}`
      })
      // User doesn't have access to the specified environment (or it doesn't exist)
      return '/app'
    }
  } catch (e) {
    console.error(e)
    return '/app'
  }
}

export const beforeFeature: NavigationGuardWithThis<undefined> = (to) => {
  const globalStore = useGlobalStore()
  const { currentMainFeatureId, featureConfigs } = storeToRefs(globalStore)
  const { parameterValues } = storeToRefs(useParametersStore())

  if (to.params.featureId) {
    // Make sure feature is set in store
    currentMainFeatureId.value = `${to.params.featureId}`
  } else {
    // Route to first feature in case feature not defined in route
    const { mainConfigs } = featureConfigs.value

    // Detect mobile using same breakpoint as Quasar ($breakpoint-sm: 600px)
    const isMobile = globalThis.matchMedia('(max-width: 599px)').matches

    // Create context for hide expression evaluation
    const context = createHideExpressionContext(parameterValues.value)

    // Find first feature that is not dummy and passes all visibility checks
    const firstFeatureConfig = mainConfigs.find(
      (c) => !c.dummy && isFeatureVisible(c.feature, isMobile, context)
    )

    if (firstFeatureConfig?.feature) {
      currentMainFeatureId.value = `${firstFeatureConfig.feature.$dtId}`
      return {
        name: 'app-feature',
        params: {
          environmentId: to.params.environmentId,
          featureId: firstFeatureConfig.feature.$dtId
        },
        query: to.query
      }
    }
  }
}
